// src/components/SortableTh.tsx
//
// One sortable column header, shared by every table that sorts on the
// server (BillsListPage, AuditPage) rather than three slightly different
// copies. The header is a real <button> inside the <th> — keyboard-reachable
// and announced as actionable — and `aria-sort` lives on the <th> itself,
// which is the whole accessibility story for a sortable table (WAI-ARIA's
// sortable-table pattern; a screen reader announces the column's sort state
// from that one attribute, not from the arrow glyph, which is `aria-hidden`).
//
// Clicking cycles ascending -> descending -> the server's own default order
// (`useSort`'s `column: null`) — a user who sorts by accident gets back to
// where they started without a page reload.
//
// Focus-ring clearance: `.pt-th-sort` (styles/index.css) sets `padding: 0`
// and does NOT touch the <th>'s own padding (`var(--sgds-spacer-4)
// var(--sgds-spacer-5)` = 12px/16px, read from the installed
// @govtechsg/sgds-web-component package's themes/root.css, not memorised —
// see docs/design/sgds-reconciliation.md §2). The button fills the CONTENT
// box that padding leaves behind, so the focus ring's ~4px protrusion
// (2px outline + 2px outline-offset, index.css's base `:focus-visible`) sits
// 8-12px inside the <th>'s own boundary — comfortably past the project's 6px
// minimum (index.css's "Focus-ring clearance" section) on every side: the
// neighbouring header's text, and the row below. Verified by measurement
// (getBoundingClientRect) in the Playwright pass behind
// docs/design/screenshots/app-19-sorted-bills.png, not assumed because the
// arithmetic looked adequate on paper — this project has a real defect on
// record from exactly that assumption.
import { useId, useState } from 'react'
import { sortAnnouncement, type SortDirection, type SortState } from '../lib/useSort'

function SortArrow({ active, dir }: { active: boolean; dir: SortDirection }) {
  return (
    <svg
      aria-hidden="true"
      width="10"
      height="10"
      viewBox="0 0 10 10"
      className={active ? 'pt-th-sort-arrow pt-th-sort-arrow--active' : 'pt-th-sort-arrow'}
      style={active && dir === 'desc' ? { transform: 'rotate(180deg)' } : undefined}
    >
      <path d="M5 2 L8.5 7 L1.5 7 Z" fill="currentColor" />
    </svg>
  )
}

export interface SortableThProps<K extends string> {
  readonly label: string
  readonly sortKey: K
  readonly sort: SortState<K>
  readonly onSort: (column: K) => void
  /** Right-aligns the header (and its button's content) — for a numeric column. */
  readonly align?: 'right'
  /**
   * A longer form of the label, shown on hover and read as the button's accessible
   * description.
   *
   * ⚠ Added for `AccessControlMatrixPage`'s role columns, which abbreviate
   * `PSD_USER` to `PSD` so nine of them fit across the grid. It sits on the BUTTON
   * rather than the `<th>` — where the plain header carried it before this component
   * replaced it — because the button fills the header's content box, so a `title` on
   * the `<th>` would never be the element under the pointer.
   */
  readonly title?: string
}

export function SortableTh<K extends string>({ label, sortKey, sort, onSort, align, title }: SortableThProps<K>) {
  const active = sort.column === sortKey
  const ariaSort: 'ascending' | 'descending' | 'none' = !active ? 'none' : sort.dir === 'asc' ? 'ascending' : 'descending'

  return (
    <th scope="col" aria-sort={ariaSort} className={align === 'right' ? 'pt-num' : undefined}>
      <button
        type="button"
        className={active ? 'pt-th-sort pt-th-sort--active' : 'pt-th-sort'}
        onClick={() => onSort(sortKey)}
        {...(title !== undefined && { title })}
      >
        <span>{label}</span>
        <SortArrow active={active} dir={sort.dir} />
      </button>
    </th>
  )
}

/**
 * The spoken half of a sort.
 *
 * `aria-sort` on the `<th>` tells a screen reader the column's state when the
 * user is ON that header — it says nothing at the moment the order actually
 * changes, and a sighted user sees a whole table reorder that a screen-reader
 * user is given no signal for at all. This live region supplies that signal
 * once, when the new order has actually arrived from the server.
 *
 * Rendered next to the table, never inside it: a live region inside `<table>`
 * markup is invalid where it lands (between `<thead>` and `<tbody>`, say) and
 * some screen readers stop honouring `aria-live` on nodes with table display
 * roles.
 */
export function SortAnnouncement<K extends string>({
  sort,
  interacted,
  busy,
  columnLabels,
  rowCount,
}: {
  readonly sort: SortState<K>
  readonly interacted: boolean
  readonly busy: boolean
  readonly columnLabels: Readonly<Record<K, string>>
  readonly rowCount: number
}) {
  return (
    <div role="status" aria-live="polite" className="sr-only">
      {sortAnnouncement(sort, interacted, busy, columnLabels, rowCount)}
    </div>
  )
}

/**
 * ★ WHY THIS COLUMN DOES NOT SORT, said where an officer can read it (UAT re-run F-13).
 *
 * The reason lived only in a `data-unsortable` attribute, which a sighted officer never sees and a screen reader never
 * reads. Put this inside the `<th data-unsortable="...">`: it reads the reason from that SAME attribute (so there is one
 * copy of the sentence, and the census that requires the attribute still governs it) and shows it as a tooltip on hover
 * AND on keyboard focus, named to assistive technology with `aria-describedby`. A 24 px target (WCAG 2.5.8).
 */
export function UnsortableHint() {
  const id = useId()
  const [reason, setReason] = useState('')
  return (
    <span
      className="pt-why"
      ref={(node) => {
        const text = node?.closest('th')?.getAttribute('data-unsortable')
        if (text !== undefined && text !== null && text !== reason) setReason(text)
      }}
    >
      <span className="pt-why-trigger" tabIndex={0} aria-describedby={id}>
        <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false">
          <circle cx="8" cy="4.2" r="1.2" fill="currentColor" />
          <path d="M8 7v5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
        <span className="sr-only">Why this column does not sort</span>
      </span>
      <span id={id} role="tooltip" className="pt-why-tip">
        {/^Sorting /.test(reason) ? reason : `This column does not sort: ${reason}`}
      </span>
    </span>
  )
}
